import { useCallback, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import submitConchBatch from 'consumers/httpSubmitBatch';
import { compressParams } from 'containers/RequestReport/utils';
import { useSelector } from 'react-redux';
import selectGgEnvironment from 'selectors/ggEnvironment';
import selectTokenRoles from 'selectors/sessionTokenRoles';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';
import { exceededRequestReportQuota, getJobQueue } from 'utils/conchUtils';
import { hasSuperAdminRole } from 'utils/roleUtils';

import { ReportParamsV2 } from '../params/buildParams';

export type SubmitOutcome =
  | { ok: true }
  | {
      ok: false;
      reason: 'busy' | 'quota' | 'rejected' | 'error';
      message: string;
    };

export const jobNameFor = (
  params: Pick<
    ReportParamsV2,
    'client_name' | 'project_name' | 'target_group_name'
  >,
) =>
  [params.client_name, params.project_name, params.target_group_name]
    .map((part) => String(part ?? '').replace(/ /g, '-'))
    .join('_');

/**
 * Sends report params to conch batch, mirroring `requestReportFormSubmit`
 * (quota check for non-superadmins, gzipped params, queue by core count).
 */
const useSubmitReport = (viewerId: number | undefined) => {
  const ggEnvironment = useSelector(selectGgEnvironment);
  const roles = useSelector(selectTokenRoles);
  const workspaceProducerId = useSelector(selectWorkspaceProducerId);
  const [submitting, setSubmitting] = useState(false);
  // A ref, not state, so a second click in the same tick still sees the first submission.
  const inFlight = useRef(false);

  const submit = useCallback(
    async (params: ReportParamsV2): Promise<SubmitOutcome> => {
      if (inFlight.current) {
        return {
          ok: false,
          reason: 'busy',
          message: 'A submission is already in progress.',
        };
      }
      inFlight.current = true;
      setSubmitting(true);
      try {
        if (!hasSuperAdminRole(roles)) {
          const exceeded = await exceededRequestReportQuota(
            viewerId,
            Number(workspaceProducerId),
          );
          if (exceeded) {
            return {
              ok: false,
              reason: 'quota',
              message:
                'This workspace has reached its daily report request limit. Try again tomorrow or contact product support.',
            };
          }
        }
        const response = await submitConchBatch(
          ggEnvironment,
          jobNameFor(params),
          params.job_definition,
          getJobQueue(params.numCores),
          params,
          compressParams(JSON.stringify(params)),
        );
        if (!response.ok) {
          Sentry.captureException(
            new Error(
              `Report Request V2 submission failed: ${response.statusText}`,
            ),
            {
              contexts: {
                response: {
                  status: response.status,
                  statusText: response.statusText,
                  url: response.url,
                },
              },
            },
          );
          return {
            ok: false,
            reason: 'rejected',
            message:
              response.statusText || `Request failed (${response.status})`,
          };
        }
        return { ok: true };
      } catch (error) {
        Sentry.captureException(error);
        return {
          ok: false,
          reason: 'error',
          message: error instanceof Error ? error.message : String(error),
        };
      } finally {
        inFlight.current = false;
        setSubmitting(false);
      }
    },
    [ggEnvironment, roles, viewerId, workspaceProducerId],
  );

  return { submit, submitting };
};

export default useSubmitReport;
